import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { isEmpty, startCase, uniqBy } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import useProfitPoolFiltersLoading from './useProfitPoolFiltersLoading';
import { useProfitPoolOverviewContext } from '../context/ProfitPoolOverviewContext';
import { fetchCurrency, formatNumWithKMBAlt, getConfig } from 'utils/helperMethods';
import fetchProfitPoolData, { PROFIT_POOL_API_LIMIT } from '../apiUtils';
import { profitPoolUrls } from 'config/urls';
import { FilterContextData } from 'contexts/FilterContext';
import { UserContext } from 'contexts/ConfigContext';
import { TableColumnsType } from 'antd';

export interface StackedBarChartDataItem {
    attribute_ex_id: string;
    attributeDesc: string;
    abs_profit: number;
    tier: string;
    totalVal: number;
    index: number;
    color: string;
}

export interface ProfitPoolTableDataItem {
    key: string;
    attributeDesc: string;
    [key: string]: string | number;
}

export type ProfitPoolTableColumnType = TableColumnsType<ProfitPoolTableDataItem>;

const getProfitPoolbyHierarchyTableDefinition = (
    tierRef: string,
    tierName: string,
    currencySymbol: string,
): ProfitPoolTableColumnType[number] => {
    return {
        title: tierName,
        dataIndex: tierRef,
        key: tierRef,
        align: 'center' as const,
        render: (value: any, record: any) => {
            return (
                <div className="flex flex-row items-center justify-between">
                    <p>{`${currencySymbol} ${value}`}</p>
                    <p className="text-xs text-[#00000073]">{record[`${tierRef}Percent`]}</p>
                    <div />
                </div>
            );
        },
    };
};

const getTabledata = (responseData: any, multiSelectedTiers: string[]) => {
    const grouped = responseData.reduce((acc: any, item: any) => {
        const attributeDesc = item.attribute_ex_id;
        if (!acc[attributeDesc]) {
            // Dynamically initialize with all selected tiers
            const initialTiers = multiSelectedTiers.reduce((tierAcc: any, tier: string) => {
                tierAcc[tier] = 0;
                return tierAcc;
            }, {});
            acc[attributeDesc] = { attributeDesc, ...initialTiers };
        }

        // Extract KPI value (if exists)
        const val =
            item.kpi_values?.profit_equivalent ?? item.kpi_values?.profit_absolute ?? item.kpi_values?.profit_unit ?? 0;

        // Map tier name to property key dynamically
        if (multiSelectedTiers.includes(item.tier)) {
            acc[attributeDesc][item.tier] = val;
        }

        return acc;
    }, {});

    // Step 2: convert grouped object to desired array format
    const result = Object.values(grouped).map((item: any, index: number) => {
        const resultItem: any = {
            key: String(index + 1),
            attributeDesc: item.attributeDesc,
        };

        // Calculate sum of all tier values for this row
        const tierSum = multiSelectedTiers.reduce((sum: number, tier: string) => {
            return sum + (item[tier] ?? 0);
        }, 0);

        // Dynamically add formatted tier values and percentages
        multiSelectedTiers.forEach((tier: string) => {
            const tierValue = item[tier] ?? 0;
            resultItem[tier] = formatNumWithKMBAlt(tierValue);

            // Calculate tier percentage: (tier value / sum of all tiers) * 100
            const tierPercent = tierSum !== 0 ? (tierValue / tierSum) * 100 : 0;
            resultItem[`${tier}Percent`] = `${tierPercent.toFixed(2)}%`;
        });

        return resultItem;
    });

    return result;
};

const useProfitPoolbyHierarchyDataInfiniteScroll = () => {
    const { appConfig } = useContext(UserContext);
    const currencySymbol = fetchCurrency(appConfig) || '';
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { tiersColorMapPPOverview } = profitPoolConfig;
    const [stackedBarChartData, setStackedBarChartData] = useState<StackedBarChartDataItem[]>([]);
    const [profitPoolTableData, setProfitPoolTableData] = useState<ProfitPoolTableDataItem[]>([]);
    const [profitPoolTableDefinition, setProfitPoolTableDefinition] = useState<ProfitPoolTableColumnType>([
        {
            title: 'Brands',
            dataIndex: 'attributeDesc',
            key: 'attributeDesc',
        },
    ]);
    const [groupedChartData, setGroupedChartData] = useState<any[]>([]);

    const { selectedSubFilter, selecedPPOUnit } = useProfitPoolOverviewContext();
    const {
        multiSelectedTiers,
        profitPoolTierList,
        idForRestOfComponent,
        allDimAndAttributeTypeList,
        timePeriodLoading,
        timePeriodSelection,
        idForRestOfComponentLoading,
        presetsFilterObj,
    } = useContext(FilterContextData);

    const [min, setMin] = useState<number>(0);
    const [max, setMax] = useState<number>(0);

    const isFiltersLoading = useProfitPoolFiltersLoading();

    const { data, isPending, isError, isSuccess, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
        useInfiniteQuery({
        queryKey: [
            'profit-pool-by-hierarchy-infi',
            selectedSubFilter?.dataKey,
            selecedPPOUnit,
            multiSelectedTiers,
            idForRestOfComponent,
            timePeriodSelection,
        ],
        queryFn: ({ pageParam, signal }) =>
            fetchProfitPoolData({
                selectedSubFilter,
                offset: pageParam as number,
                limit: PROFIT_POOL_API_LIMIT,
                kpi: selecedPPOUnit,
                tierList: multiSelectedTiers,
                yearAgo: false,
                groupedByList: ['attribute'],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                absValueSortOrder: 'desc', //sortorder for profitpool by hirearchy data
                absValueSortKpi: 'volume_sales_equivalent', 
                signal,
            }),
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            return lastPageParam + PROFIT_POOL_API_LIMIT;
        },
        initialPageParam: 0,
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                allDimAndAttributeTypeList.loading === false &&
                !timePeriodLoading &&
                timePeriodSelection &&
                !idForRestOfComponentLoading &&
                presetsFilterObj !== undefined,
        ),
    });

    useEffect(() => {
        if (!isPending && data?.pages) {
            // Flatten all pages into a single array
            const allData = data.pages.flatMap((page: any) => page?.data || []);

            if (!isEmpty(allData)) {
                const attributeMap = new Map();

                // Pre-compute totals and per-attribute positive/negative extents.
                // In a stacked bar with mixed signs, positive values stack upward and
                // negative values stack downward — the Y-axis must fit both extents,
                const attributeTotals: Record<string, number> = {};
                const attributePositiveExtent: Record<string, number> = {};
                const attributeNegativeExtent: Record<string, number> = {};

                allData.forEach((item: any) => {
                    const attributeDesc = item.attribute_desc;
                    const profit = item.kpi_values?.[selecedPPOUnit] || 0;
                    attributeTotals[attributeDesc] = (attributeTotals[attributeDesc] || 0) + profit;
                    if (profit > 0) {
                        attributePositiveExtent[attributeDesc] = (attributePositiveExtent[attributeDesc] || 0) + profit;
                    } else {
                        attributeNegativeExtent[attributeDesc] = (attributeNegativeExtent[attributeDesc] || 0) + profit;
                    }
                });

                let minProfit = Number.POSITIVE_INFINITY;
                let maxProfit = Number.NEGATIVE_INFINITY;
                for (const attr of Object.keys(attributeTotals)) {
                    const total = attributeTotals[attr] || 0;
                    const posExtent = attributePositiveExtent[attr] || 0;
                    const negExtent = attributeNegativeExtent[attr] || 0;
                    minProfit = Math.min(minProfit, negExtent, total);
                    maxProfit = Math.max(maxProfit, posExtent, total);
                }

                // Assign a stable numeric index per x-axis bucket (attribute), used by StackedBarChart for interval labels
                const attributeIndexMap = new Map<string, number>();
                let attributeIndex = 0;
                allData.forEach((item: any) => {
                    const key = item.attribute_desc;
                    if (!attributeIndexMap.has(key)) {
                        attributeIndexMap.set(key, attributeIndex);
                        attributeIndex += 1;
                    }
                });

                const finalChartData = allData.map((item: any) => {
                    const currentProfit = item.kpi_values?.[selecedPPOUnit] || 0;
                    const currentTotalVal = attributeTotals[item.attribute_desc] || 0;

                    const createdObjForAttr = {
                        attribute_ex_id: item.attribute_ex_id,
                        attributeDesc: item.attribute_desc,
                        abs_profit: currentProfit,
                        tier: item.tier,
                        color: tiersColorMapPPOverview[item?.tier],
                        totalVal: currentTotalVal,
                        index: attributeIndexMap.get(item.attribute_desc) ?? 0,
                    };
                    if (!attributeMap.has(item.attribute_ex_id)) {
                        attributeMap.set(item.attribute_ex_id, []);
                    }
                    const currentAttributeVal = attributeMap.get(item.attribute_ex_id);
                    currentAttributeVal.push(createdObjForAttr);
                    attributeMap.set(item.attribute_ex_id, currentAttributeVal);
                    return createdObjForAttr;
                });

                setMin(minProfit === Number.POSITIVE_INFINITY ? 0 : minProfit);
                setMax(maxProfit === Number.NEGATIVE_INFINITY ? 0 : maxProfit);
                const tableData = getTabledata(allData, multiSelectedTiers);

                const tableDefinition = multiSelectedTiers.map((tier) =>
                    getProfitPoolbyHierarchyTableDefinition(
                        tier,
                        profitPoolTierList.find((t) => t.tierReference === tier)?.tierName,
                        currencySymbol,
                    ),
                );
                setProfitPoolTableDefinition(
                    uniqBy(
                        [
                            ...[
                                {
                                    title: startCase(selectedSubFilter?.dataKey),
                                    dataIndex: 'attributeDesc',
                                    key: 'attributeDesc',
                                },
                            ],
                            ...tableDefinition,
                        ],
                        'title',
                    ),
                );
                setProfitPoolTableData(tableData);
                setStackedBarChartData(finalChartData);
                setGroupedChartData(Array.from(attributeMap.values()));
            } else {
                setProfitPoolTableData([]);
                setStackedBarChartData([]);
                setGroupedChartData([]);
                setMin(0);
                setMax(0);
                setProfitPoolTableDefinition([
                    {
                        title: 'Brands',
                        dataIndex: 'attributeDesc',
                        key: 'attributeDesc',
                    },
                ]);
            }
        }
    }, [isPending, data]);

    const rawData = useMemo(() => data?.pages?.flatMap((page: any) => page?.data || []) ?? [], [data]);
    const isDataSyncing = isSuccess && rawData.length > 0 && stackedBarChartData.length === 0;
    const isLoading = isPending || isFetching || isFiltersLoading || isDataSyncing;

    return {
        data: stackedBarChartData,
        tableData: profitPoolTableData,
        tableDefinition: profitPoolTableDefinition,
        groupedChartData,
        min,
        max,
        isPending,
        isLoading,
        isError,
        isSuccess,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    };
};

export default useProfitPoolbyHierarchyDataInfiniteScroll;
